import { Box, Typography, Button as MuiButton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Button from 'components/buttons/Button'
import Popup from 'components/popup/Popup'
import PopupSection from 'components/popup/PopupSection'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInput, { CountryData } from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { userLang } from 'utils/lang'
import { extractCountryCode } from 'utils/phone'

interface AddMemberByPhonePopupProps {
  children: ReactNode
  open: boolean
  setOpen: (open: boolean) => void
  onAddMember: (phone: string) => void
}

export function AddMemberByPhonePopupContent(props: {
  onAddMember: (phone: string) => void
  setOpen: (open: boolean) => void
}) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [phone, setPhone] = useState('')
  const [country] = useState<string | undefined>(userLang())
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const addButtonDisabledColor =
    theme.palette.mode === 'dark'
      ? theme.palette.action.disabledBackground
      : theme.palette.button.action.disabled
  const addButtonDisabledTextColor =
    theme.palette.mode === 'dark'
      ? theme.palette.text.disabled
      : theme.palette.button.action.contrastText

  useEffect(() => {
    if (phone.length === 0)
      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
  }, [phone])

  const handleAdd = () => {
    if (isValidPhoneNumber(`+${phone}`)) {
      props.onAddMember(`+${phone}`)

      // keep previous phone number country code for next add
      const countryCode = extractCountryCode(`+${phone}`)
      setPhone(countryCode.slice(1)) // Remove + as PhoneInput handles it

      setTimeout(() => {
        phoneInputRef.current?.focus()
      }, 100)
    }
  }

  // on enter click, add member in case phone number is valid
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleAdd()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [phone])

  const onPhoneNumberChange = (value: string, data: CountryData) => {
    // Move cursor to end after state update
    setPhone(value)

    setTimeout(() => {
      if (phoneInputRef.current) {
        const input = phoneInputRef.current
        if (input.value.length <= 2)
          input.setSelectionRange(input.value.length, input.value.length)
      }
    }, 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const input = e.target as HTMLInputElement
      const cursorPosition = input.selectionStart || 0
      const value = input.value

      // Check if cursor is right after a formatting character
      const formatChars = ['(', ')', ' ', '-']
      const charBeforeCursor = value[cursorPosition - 1]

      if (formatChars.includes(charBeforeCursor)) {
        e.preventDefault()

        // Find the last digit before formatting characters
        let newPosition = cursorPosition - 1
        while (
          newPosition > 0 &&
          formatChars.includes(value[newPosition - 1])
        ) {
          newPosition--
        }

        // Remove the digit and let the library reformat
        const newValue =
          value.slice(0, newPosition - 1) + value.slice(cursorPosition)
        const cleanValue = newValue.replace(/\D/g, '') // Remove all non-digits

        // Update the phone value
        const countryData =
          (window as any).intlTelInputGlobals?.getCountryData?.() || {}
        onPhoneNumberChange(cleanValue, countryData)
      }
    }
  }

  return (
    <Box
      sx={{
        direction: 'ltr',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Box
        sx={{
          direction: 'ltr',
          display: 'flex',
          flex: 1,
          minWidth: 0,

          '& .react-tel-input .selected-flag:hover, & .react-tel-input .selected-flag.open':
            {
              backgroundColor: 'var(--color-surface-subtle)'
            },
          '& .react-tel-input .selected-flag': {
            backgroundColor: 'var(--color-surface-subtle)',
            borderRight: `1px solid ${theme.palette.border.primary}`,

            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px'
          },
          '& .react-tel-input .flag-dropdown:after': {
            borderTop: `4px solid ${theme.palette.text.secondary}`,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent'
          }
        }}
      >
        <PhoneInput
          enableSearch
          enableTerritories
          placeholder={t('phoneNumberPlaceholder', 'Phone number')}
          inputProps={{ ref: phoneInputRef }}
          inputStyle={{
            direction: 'ltr',
            width: '100%',
            height: '30px',
            borderRadius: '8px 0 0 8px',
            border: `1px solid ${theme.palette.border.primary}`,
            fontSize: '14px',
            paddingLeft: '48px',
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.card.primary,
            boxSizing: 'border-box'
          }}
          buttonStyle={{
            borderTopLeftRadius: '8px',
            borderBottomLeftRadius: '8px',
            border: 'none',

            backgroundColor: 'var(--color-surface-subtle)',

            cursor: 'pointer'
          }}
          containerStyle={{
            width: '100%'
          }}
          dropdownStyle={{
            width: '300px',
            borderRadius: '8px'
          }}
          country={country}
          value={phone}
          onChange={onPhoneNumberChange}
          onKeyDown={handleKeyDown}
        />
      </Box>

      <MuiButton
        variant="contained"
        onClick={handleAdd}
        disabled={!isValidPhoneNumber(`+${phone}`)}
        sx={{
          height: '30px',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          padding: '0 10px',
          flexShrink: 0,
          minWidth: 'fit-content',
          whiteSpace: 'nowrap',
          borderRadius: '0 8px 8px 0',
          marginLeft: 0,
          color: theme.palette.button.action.contrastText,
          backgroundColor: theme.palette.button.action.main,
          '&:hover': {
            backgroundColor: theme.palette.button.action.hover
          },
          '&:active': {
            backgroundColor: theme.palette.button.action.active
          },
          '&:disabled': {
            backgroundColor: addButtonDisabledColor,
            color: addButtonDisabledTextColor
          }
        }}
      >
        {t('add', 'Add')}
      </MuiButton>
    </Box>
  )
}

export default function AddMemberByPhonePopup(
  props: AddMemberByPhonePopupProps
) {
  return (
    <Popup
      padding={0}
      closeOnTouchOutside
      open={props.open}
      setOpen={props.setOpen}
      placement="left"
      content={
        <AddMemberByPhonePopupContent
          onAddMember={props.onAddMember}
          setOpen={props.setOpen}
        />
      }
    >
      {props.children}
    </Popup>
  )
}
